import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// ════════════════════════════════════════════════════════════════════════════
// BrandingContext — diffuse le branding personnalisé du cabinet dans l'app
// ════════════════════════════════════════════════════════════════════════════
// Source de vérité : localStorage('lexback_branding') alimenté par la page
// /branding. Cette source est lue au démarrage et écoutée pour les changements
// (évènement 'storage' pour les autres onglets + évènement custom 'branding:updated'
// pour le même onglet, déclenché par la page Branding après save).
//
// Toute consommation : `const { branding } = useBranding()` — branding ne
// peut jamais être null (fallback DEFAULT_BRANDING).
// ════════════════════════════════════════════════════════════════════════════

export const DEFAULT_BRANDING = {
  nom_commercial: '',
  signature_email: '',
  logo_url: '',
  favicon_url: '',
  couleur_primaire: '#B8860B',
  couleur_secondaire: '#1A1916',
  couleur_accent: '#1A55A0',
  font_titre: 'Syne',
  font_corps: 'DM Sans',
  hide_lexback_branding: false,
  email_expediteur: '',
  telephone_support: '',
  url_site: '',
};

function readFromStorage() {
  try {
    const raw = localStorage.getItem('lexback_branding');
    if (!raw) return DEFAULT_BRANDING;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_BRANDING, ...parsed };
  } catch {
    return DEFAULT_BRANDING;
  }
}

// Applique le branding au document (CSS vars + favicon) — idempotent.
export function applyBrandingToDocument(b) {
  if (!b || typeof document === 'undefined') return;
  const root = document.documentElement;
  if (b.couleur_primaire) {
    root.style.setProperty('--brand-primary', b.couleur_primaire);
    // Dérive aussi une version atténuée (12% opacity) pour les fonds de badges/pills
    root.style.setProperty('--brand-primary-bg', hexToRgbaCss(b.couleur_primaire, 0.12));
  }
  if (b.couleur_secondaire) root.style.setProperty('--brand-secondary', b.couleur_secondaire);
  if (b.couleur_accent)     root.style.setProperty('--brand-accent',    b.couleur_accent);
  if (b.font_titre)         root.style.setProperty('--brand-font-title', `'${b.font_titre}', sans-serif`);
  if (b.font_corps)         root.style.setProperty('--brand-font-body',  `'${b.font_corps}', sans-serif`);
  if (b.favicon_url) {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = b.favicon_url;
  }
}

// hex → rgba CSS string
function hexToRgbaCss(hex, a) {
  if (!hex || !/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) return `rgba(199,140,42,${a})`;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

// Écrit en storage + diffuse aux abonnés dans le même onglet
export function saveBranding(branding) {
  try {
    localStorage.setItem('lexback_branding', JSON.stringify(branding));
    applyBrandingToDocument(branding);
    // Évènement custom pour notifier les hooks dans le même onglet
    // (l'évènement 'storage' natif ne se déclenche que pour les AUTRES onglets)
    window.dispatchEvent(new CustomEvent('branding:updated', { detail: branding }));
  } catch (e) {
    console.warn('[Branding] save failed', e);
  }
}

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  setBranding: () => {},
});

export function BrandingProvider({ children }) {
  const [branding, setBrandingState] = useState(readFromStorage);

  // Applique au montage
  useEffect(() => {
    applyBrandingToDocument(branding);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Écoute les changements (autre onglet via 'storage' + même onglet via 'branding:updated')
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'lexback_branding') {
        const b = readFromStorage();
        setBrandingState(b);
        applyBrandingToDocument(b);
      }
    };
    const onCustom = (e) => {
      const b = e.detail || readFromStorage();
      setBrandingState(b);
      applyBrandingToDocument(b);
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('branding:updated', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('branding:updated', onCustom);
    };
  }, []);

  const setBranding = useCallback((next) => {
    setBrandingState(next);
    saveBranding(next);
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, setBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}

// Utilitaire : contraste auto pour les boutons primaires
export function getContrastTextColor(hex) {
  if (!hex || typeof hex !== 'string') return '#FFFFFF';
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return '#FFFFFF';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#1A1916' : '#FFFFFF';
}
